import React, { Component } from 'react'
import { gql } from '@apollo/client'
import { observable } from 'mobx'
import { observer } from 'mobx-react'
import { func, string } from 'prop-types'

import Select from 'components/ReactSelect'
import { client } from 'lib'
import { Filters } from 'utils'

import './index.scss'

const ALL_TOPICS_QUERY = gql`
  query ALL_TOPICS_QUERY($language: String!) {
    allTopics(language: $language) {
      entities {
        title
        topicId
        createdAt
      }
    }
  }
`

const ALL_TOPIC_GROUPS_QUERY = gql`
  query ALL_TOPIC_GROUPS_QUERY($language: String!) {
    allTopicGroups(language: $language) {
      title
      topicGroupId
    }
  }
`

const TOPICS_BY_CATEGORY_QUERY = gql`
  query TOPICS_BY_CATEGORY_QUERY($groupIds: [Int!]!) {
    topicByGroup(groupIds: $groupIds) {
      topicId
      title
      createdAt
    }
  }
`

const sortByLabel = (a, b) => a.label.localeCompare(b.label)

@observer
class TopicSelector extends Component {
  @observable
  options = []

  @observable
  loading = false

  componentDidMount() {
    this.props.filteredCategoryId ? this.applyCategoryFilter(this.props.filteredCategoryId) : this.getAllTopics()
  }

  componentDidUpdate(prevProps) {
    if (this.props.filteredCategoryId !== prevProps.filteredCategoryId) {
      this.applyCategoryFilter(this.props.filteredCategoryId)
    }
    if (this.props.language !== prevProps.language) {
      this.getAllTopics()
    }
  }

  topicToOptionValue = t => {
    const { topicGroup } = this.props
    return { label: t.title, value: topicGroup ? t.topicGroupId : t.topicId }
  }

  async applyCategoryFilter(filteredCategoryId) {
    const { topicGroup } = this.props
    if (filteredCategoryId) {
      this.loading = true
      try {
        const query = TOPICS_BY_CATEGORY_QUERY
        const variables = { groupIds: filteredCategoryId }
        const topics = await client.query({ query, variables })
        const {
          data: { topicByGroup },
        } = topics
        const options = topicByGroup?.map(this.topicToOptionValue)

        if (this.props.isTarget) {
          this.loading = false
          this.options = options
          return this.options
        }

        options.unshift({
          label: 'All',
          value: [...topicByGroup.map(t => (topicGroup ? t.topicGroupId : t.topicId))],
        })
        this.loading = false
        return (this.options = options)
      } catch (e) {
        console.warn('Error getting category topics >>>>>>>>>>>', e)
      }
    }
    this.getAllTopics()
  }

  getAllTopics() {
    const { topicGroup } = this.props
    const query = topicGroup ? ALL_TOPIC_GROUPS_QUERY : ALL_TOPICS_QUERY
    const defaultLanguage = Filters.getDefaultFilter({ group: 'topics', filter: 'language' }).value
    const { language = defaultLanguage } = this.props
    this.loading = true
    client
      .query({
        query,
        variables: {
          language,
        },
      })
      .then(({ data }) => {
        const entities = topicGroup ? data?.allTopicGroups : data?.allTopics?.entities
        let options = entities.map(this.topicToOptionValue)
        options = options.sort(sortByLabel)
        if (this.props.isTarget) {
          this.loading = false
          this.options = options
          return this.options
        }
        options.unshift({ label: 'All', value: '' })
        this.loading = false
        return (this.options = options)
      })
      .catch(e => {
        console.warn('error getting topics for topicSelector >>>>>>>>>>>', e)
        this.loading = false
      })
  }

  render() {
    const { options = [] } = this
    const {
      className,
      value,
      onChange,
      placeholder = 'Topics',
      label,
      languageSelector,
      languageSelectorValue,
      languageSelectorOptions,
      onLanguageChange,
    } = this.props

    return languageSelector ? (
      <div className="topic-selector-language-group">
        <Select
          containerClass="language-selector"
          onChange={onLanguageChange}
          placeholder="Language"
          value={languageSelectorValue}
          name="select-filter-language"
          options={languageSelectorOptions}
          icon="globe"
          isOptionDisabled={option => option.disabled === true}
        />
        <Select
          className={className}
          onChange={option => onChange(option)}
          placeholder={placeholder}
          value={value}
          name="select-filter-topics"
          options={options}
          label={label}
          {...this.props}
          isDisabled={this.loading}
        />
      </div>
    ) : (
      <Select
        className={className}
        onChange={option => onChange(option)}
        placeholder={placeholder}
        value={value}
        name="select-filter-topics"
        options={options}
        label={label}
        {...this.props}
        isDisabled={this.loading}
      />
    )
  }
}

TopicSelector.propTypes = {
  className: string,
  onChange: func.isRequired,
  placeholder: string,
  language: string.isRequired,
}

TopicSelector.defaultProps = {}

TopicSelector.displayName = 'TopicSelector'

export default TopicSelector
